import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice.js';
import { toast } from 'react-hot-toast';
import { Package, Save, X, Eye, Users } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout.jsx';
import NewShipmentRecipientsModal from '../components/dashboard/NewShipmentRecipientsModal.jsx';
import ManifestSection from '../components/dashboard/ShipmentForm/ManifestSection.jsx';
import DetailsSection from '../components/dashboard/ShipmentForm/DetailsSection.jsx';
import EventSection from '../components/dashboard/ShipmentForm/EventSection.jsx';
import SelectionModals from '../components/dashboard/ShipmentForm/SelectionModals.jsx';
import { CREATE_COMPLETE_SHIPMENT_MUTATION } from '../services/graphql/mutations.js';
import { ORIGINS_QUERY, DESTINATIONS_QUERY, SUPPLIERS_QUERY, USERS_QUERY } from '../services/graphql/queries.js';

const CreateNewShipment = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    shipment: {
      manifest: '',
      reference: '',
      originId: '',
      destinationId: '',
      driver: '',
      driver_passport: '',
      driver_license: '',
      driver_phone: '',
      notes: ''
    },
    details: [
      {
        bill_of_lading: '',
        commercial_invoice: '',
        consigneeId: '',
        suppliers: [],
        ff: null,
        cif: false,
        additional_consignees: '',
        seller: null,
        isManualConsignee: false,
        isTransportistaConsignee: false
      }
    ],
    initialEvent: {
      status_detail: '',
      status_date: new Date().toISOString(),
      approved: false
    }
  });

  // Estados del componente
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showRecipientsPreview, setShowRecipientsPreview] = useState(false);
  const [showModals, setShowModals] = useState({
    origins: false,
    destinations: false,
    suppliers: false,
    consignees: false,
    ff: false
  });
  const [currentDetailIndex, setCurrentDetailIndex] = useState(null);

  // GraphQL Queries
  const { data: originsData } = useQuery(ORIGINS_QUERY);
  const { data: destinationsData } = useQuery(DESTINATIONS_QUERY);
  const { data: suppliersData } = useQuery(SUPPLIERS_QUERY);
  const { data: usersData } = useQuery(USERS_QUERY);

  // GraphQL Mutation
  const [createCompleteShipment] = useMutation(CREATE_COMPLETE_SHIPMENT_MUTATION, {
    onError: (error) => {
      console.error('GraphQL Error:', error);

      // Extraer mensaje específico de validación
      let errorMessage = 'Error al crear el envío';

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];

        if (graphQLError.extensions?.code === 'VALIDATION_ERROR') {
          errorMessage = `⚠️ Error de validación: ${graphQLError.message}`;
        } else {
          errorMessage = graphQLError.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        duration: 6000, // Más tiempo para leer el mensaje
        position: 'top-center'
      });

      setIsSubmitting(false);
      setIsSavingDraft(false); // También resetear draft state
    }
  });

  // Función utilitaria para limpiar espacios en blanco
  const trimFormData = (data) => {
    if (typeof data === 'string') {
      return data.trim();
    }
    
    if (Array.isArray(data)) {
      return data.map(item => trimFormData(item));
    }
    
    if (data !== null && typeof data === 'object') {
      const trimmedData = {};
      for (const [key, value] of Object.entries(data)) {
        trimmedData[key] = trimFormData(value);
      }
      return trimmedData;
    }
    
    return data;
  };

  // Filtra filas de detalle completamente vacías (el usuario agregó una fila y no la llenó).
  // Se usa en los 3 caminos (vista previa/modal, crear y borrador) para que el modal de
  // destinatarios y la mutation trabajen siempre sobre la misma lista de details.
  const getValidDetails = (details) => details.filter(detail => {
    const hasBL = detail.bill_of_lading?.trim();
    const hasCI = detail.commercial_invoice?.trim();
    const hasConsignee = detail.consigneeId || detail.additional_consignees?.trim();
    const hasSuppliers = detail.suppliers && detail.suppliers.length > 0;
    return hasBL || hasCI || hasConsignee || hasSuppliers;
  });

  // Validación del formulario
  const validateForm = () => {
    const newErrors = {};
    
    // Validar campos obligatorios del manifiesto
    if (!formData.shipment.manifest.trim()) {
      newErrors.manifest = 'El número de manifiesto es requerido';
    }
    
    if (!formData.shipment.reference.trim()) {
      newErrors.reference = 'La referencia es requerida';
    }
    
    if (!formData.shipment.originId) {
      newErrors.originId = 'El origen es requerido';
    }
    
    if (!formData.shipment.destinationId) {
      newErrors.destinationId = 'El destino es requerido';
    }
    
    if (!formData.shipment.driver.trim()) {
      newErrors.driver = 'El conductor es requerido';
    }
    
    // Validar detalles (al menos uno completo y todos deben estar completos o vacíos).
    // Los errores se acumulan en una lista (L-6, AuditoriaFinal_Sesion29.md):
    // antes cada validación sobreescribía la anterior y solo se mostraba la última.
    if (!formData.details || formData.details.length === 0) {
      newErrors.details = 'Se requiere al menos un detalle de envío';
    } else {
      const detailErrors = [];

      // Verificar que al menos un detalle esté completo
      const hasCompleteDetail = formData.details.some(detail => {
        const hasConsignee = detail.consigneeId ||
          (detail.isManualConsignee && detail.additional_consignees?.trim()) ||
          (detail.isTransportistaConsignee && detail.additional_consignees?.trim());
        const hasSuppliers = detail.suppliers && detail.suppliers.length > 0;
        return detail.bill_of_lading.trim() &&
          detail.commercial_invoice.trim() &&
          hasConsignee &&
          hasSuppliers;
      });

      if (!hasCompleteDetail) {
        detailErrors.push('Se requiere al menos un detalle completo');
      }

      // Validar que no haya detalles parcialmente llenos
      formData.details.forEach((detail, index) => {
        const bl = detail.bill_of_lading?.trim() || '';
        const ci = detail.commercial_invoice?.trim() || '';
        const hasConsignee = detail.consigneeId ||
          (detail.isManualConsignee && detail.additional_consignees?.trim()) ||
          (detail.isTransportistaConsignee && detail.additional_consignees?.trim());
        const hasSuppliers = detail.suppliers && detail.suppliers.length > 0;

        // Contar cuántos campos están llenos
        const filledFields = [bl, ci, hasConsignee, hasSuppliers].filter(f => f).length;

        // Si hay campos llenos pero no todos (parcialmente lleno)
        if (filledFields > 0 && filledFields < 4) {
          detailErrors.push(`Detalle #${index + 1}: Todos los campos obligatorios deben estar completos (BL, Factura, Consignatario, al menos 1 Proveedor)`);
        }

        // Validar campos manuales de consignees si están activos
        if (detail.isManualConsignee) {
          const trimmedConsignees = detail.additional_consignees?.trim() || '';
          if (!trimmedConsignees) {
            detailErrors.push(`Detalle #${index + 1}: Debe digitar al menos un consignatario`);
          } else {
            // Validar que al menos una línea tenga contenido
            const lines = trimmedConsignees.split('\n').filter(line => line.trim());
            if (lines.length === 0) {
              detailErrors.push(`Detalle #${index + 1}: Debe digitar al menos un consignatario con contenido válido`);
            }
          }
        }

        // Validar Transportista: additional_consignees Y FF son OBLIGATORIOS
        if (detail.isTransportistaConsignee) {
          const trimmedConsignees = detail.additional_consignees?.trim() || '';
          if (!trimmedConsignees) {
            detailErrors.push(`Detalle #${index + 1}: Debe digitar consignatarios adicionales cuando selecciona un Transportista como consignatario.`);
          } else if (!detail.ff) {
            detailErrors.push(`Detalle #${index + 1}: FF (Freight Forwarder) es obligatorio cuando selecciona un Transportista como consignatario.`);
          }
        }
      });

      // Validar duplicados en Bill of Lading
      const blValues = formData.details
        .map(d => d.bill_of_lading?.trim())
        .filter(bl => bl);

      const duplicateBLs = blValues.filter((bl, index) =>
        blValues.indexOf(bl) !== index
      );

      if (duplicateBLs.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateBLs)];
        detailErrors.push(`❌ No se permiten conocimientos (BL) duplicados. Revise los siguientes: "${uniqueDuplicates.join('", "')}"`);
      }

      // Validar duplicados en Commercial Invoice
      const ciValues = formData.details
        .map(d => d.commercial_invoice?.trim())
        .filter(ci => ci);

      const duplicateCIs = ciValues.filter((ci, index) =>
        ciValues.indexOf(ci) !== index
      );

      if (duplicateCIs.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateCIs)];
        detailErrors.push(`❌ No se permiten números de factura duplicados. Revise los siguientes: "${uniqueDuplicates.join('", "')}"`);
      }

      if (detailErrors.length > 0) {
        newErrors.details = detailErrors.join('\n');
      }
    }
    
    // Validar evento inicial
    if (!formData.initialEvent.status_detail.trim()) {
      newErrors.initialEvent = 'El estado inicial es requerido';
    }
    
    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;

    // Mostrar toast si hay errores
    if (hasErrors) {
      toast.error('⚠️ Verifica todo el manifiesto porque hay errores');
    }

    return !hasErrors;
  };

  // Manejo de cambios en el formulario
  const handleChange = (section, field, value, index = null) => {
    if (section === 'details' && index !== null) {
      const newDetails = [...formData.details];
      newDetails[index] = { ...newDetails[index], [field]: value };

      // Lógica CIF: Si FF cambia a null, poner CIF en false
      if (field === 'ff' && !value) {
        newDetails[index].cif = false;
      }

      setFormData(prev => ({ ...prev, details: newDetails }));

      // Limpiar error de details cuando se modifica un BL o factura
      if ((field === 'bill_of_lading' || field === 'commercial_invoice') && errors.details) {
        setErrors(prev => ({ ...prev, details: '' }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    }

    // Limpiar errores al escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Remover supplier del array
  const handleRemoveSupplier = (detailIndex, supplierId) => {
    const newDetails = [...formData.details];
    const currentSuppliers = newDetails[detailIndex].suppliers || [];

    // Validar que no sea el último
    if (currentSuppliers.length === 1) {
      toast.error('Debe haber al menos un proveedor');
      return;
    }

    newDetails[detailIndex].suppliers = currentSuppliers.filter(s => s.id !== supplierId);
    setFormData(prev => ({ ...prev, details: newDetails }));
  };

  // Agregar nuevo detalle
  const addDetail = () => {
    setFormData(prev => ({
      ...prev,
      details: [
        ...prev.details,
        {
          bill_of_lading: '',
          commercial_invoice: '',
          consigneeId: '',
          suppliers: [],
          ff: null,
          cif: false,
          additional_consignees: '',
          seller: null,
          isManualConsignee: false,
          isTransportistaConsignee: false
        }
      ]
    }));
  };

  // Eliminar detalle
  const removeDetail = (index) => {
    if (formData.details.length > 1) {
      const newDetails = formData.details.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, details: newDetails }));
    }
  };

  // Manejar vista previa de destinatarios
  const handlePreviewRecipients = () => {
    if (!validateForm()) return;

    // Limpiar espacios en blanco antes de vista previa
    const trimmedFormData = trimFormData(formData);

    // Filtrar detalles completamente vacíos (mismo filtro que usa el modal y la mutation)
    const validDetails = getValidDetails(trimmedFormData.details);

    // Validar que haya al menos un detalle válido
    if (validDetails.length === 0) {
      toast.error('⚠️ Debe llenar al menos un detalle antes de continuar');
      return;
    }

    // 🔒 VALIDACIÓN: Si consignatarios manuales O Transportista con additional_consignees, FF es obligatorio
    const requiresFF = validDetails.filter(d =>
      d.isManualConsignee || (d.isTransportistaConsignee && d.additional_consignees?.trim())
    );
    if (requiresFF.length > 0) {
      // Verificar que todos los detalles que requieren FF lo tengan
      const missingFF = requiresFF.some(detail => !detail.ff);

      if (missingFF) {
        toast.error('Debe seleccionar un FF si digita los consignatarios manualmente o si selecciona un Transportista con consignatarios adicionales');
        return;
      }
    }

    setShowRecipientsPreview(true);
  };

  // Manejar envío final
  const handleSubmit = async (selectedRecipients) => {
    setIsSubmitting(true);
    
    // Limpiar espacios en blanco antes de procesar
    const trimmedFormData = trimFormData(formData);
    
    // selectedRecipients now comes as granular structure from modal
    const selectedRecipientsByGroup = Array.isArray(selectedRecipients) ? selectedRecipients : [];

    // 🔒 Los details SIEMPRE se construyen desde el formulario completo: la creación en BD
    // no debe depender de la selección de correos del modal (un BL sin selección se crea
    // igual, solo que no notifica a nadie). Los selectedEmails de cada grupo se adjuntan
    // por matching de bill_of_lading (no por groupIndex posicional) — mismo patrón que
    // EventUpdates/CompleteShipment, inmune a huecos o reordenamientos en byConsignee.
    const finalDetails = getValidDetails(trimmedFormData.details)
      .map(detail => {
        const group = selectedRecipientsByGroup.find(g => g.bill_of_lading === detail.bill_of_lading);
        return {
          bill_of_lading: detail.bill_of_lading,
          commercial_invoice: detail.commercial_invoice,
          consigneeId: detail.isManualConsignee ? null : (detail.consigneeId || null),
          supplierIds: detail.suppliers ? detail.suppliers.map(s => s.id) : [],
          ff: detail.ff,
          cif: detail.cif,
          // 🔒 Si es manual O Transportista, enviar additional_consignees
          additional_consignees: (detail.isManualConsignee || detail.isTransportistaConsignee)
            ? detail.additional_consignees
            : null,
          seller: detail.seller || null,
          ...(group && group.selectedEmails?.length > 0
            ? { selectedEmails: group.selectedEmails }
            : {})
        };
      });
    
    // El formulario ya pasó validateForm() antes de abrir el modal (L-11:
    // los re-chequeos silenciosos que había aquí eran inalcanzables).
    const inputData = {
      shipment: trimmedFormData.shipment,
      details: finalDetails,
      initialEvent: {
        ...trimmedFormData.initialEvent,
        approved: true
      }
    };

    try {
      const result = await createCompleteShipment({
        variables: {
          input: inputData
        }
      });
      if (result?.data) {
        toast.success('Envío creado exitosamente');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error creating shipment:', error.message);
    }
  };

  // Guardar como borrador (sin notificaciones)
  const handleSaveDraft = async () => {
    if (!validateForm()) return;
    
    setIsSavingDraft(true);
    
    try {
      // Limpiar espacios en blanco antes de guardar
      const trimmedFormData = trimFormData(formData);
      
      // Crear estructura básica de detalles sin selectedEmails
      const finalDetails = getValidDetails(trimmedFormData.details)
        .map(detail => ({
          bill_of_lading: detail.bill_of_lading,
          commercial_invoice: detail.commercial_invoice,
          consigneeId: detail.isManualConsignee ? null : (detail.consigneeId || null),
          supplierIds: detail.suppliers ? detail.suppliers.map(s => s.id) : [],
          ff: detail.ff,
          cif: detail.cif,
          // 🔒 Si es manual O Transportista, enviar additional_consignees
          additional_consignees: (detail.isManualConsignee || detail.isTransportistaConsignee)
            ? detail.additional_consignees
            : null,
          seller: detail.seller || null
          // No selectedEmails para borradores
        }));

      // Validar que haya al menos un detalle válido
      if (finalDetails.length === 0) {
        toast.error('⚠️ Debe llenar al menos un detalle antes de guardar');
        setIsSavingDraft(false);
        return;
      }

      // Validar FF obligatorio para manuales y transportistas
      const missingFF = trimmedFormData.details.some(d =>
        (d.isManualConsignee || (d.isTransportistaConsignee && d.additional_consignees?.trim())) && !d.ff
      );
      if (missingFF) {
        toast.error('Debe seleccionar un FF si digita los consignatarios manualmente o si selecciona un Transportista con consignatarios adicionales');
        setIsSavingDraft(false);
        return;
      }
      
      const inputData = {
        shipment: trimmedFormData.shipment,
        details: finalDetails,
        initialEvent: {
          ...trimmedFormData.initialEvent,
          approved: false  // Marcar como no aprobado
        }
      };
      
      const result = await createCompleteShipment({
        variables: {
          input: inputData
        }
      });

      if (result?.data) {
        toast.success('Borrador guardado exitosamente');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving draft:', error.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Cancelar y volver al dashboard
  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Nuevo Envío</h1>
              <p className="text-gray-600">Crear un nuevo envío con detalles y evento inicial</p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="space-y-6">
          {/* Sección 1: Información del Manifiesto */}
          <ManifestSection
            data={formData.shipment}
            errors={errors}
            onChange={(field, value) => handleChange('shipment', field, value)}
            onOpenModal={(modalType) => setShowModals(prev => ({ ...prev, [modalType]: true }))}
            originsData={originsData}
            destinationsData={destinationsData}
          />

          {/* Sección 2: Detalles del Envío */}
          <DetailsSection
            data={formData.details}
            errors={errors}
            onChange={(field, value, index) => handleChange('details', field, value, index)}
            onAddDetail={addDetail}
            onRemoveDetail={removeDetail}
            onRemoveSupplier={handleRemoveSupplier}
            onOpenModal={(modalType, index) => {
              setCurrentDetailIndex(index);
              setShowModals(prev => ({ ...prev, [modalType]: true }));
            }}
            suppliersData={suppliersData}
            usersData={usersData}
          />

          {/* Sección 3: Evento Inicial */}
          <EventSection
            data={formData.initialEvent}
            errors={errors}
            onChange={(field, value) => handleChange('initialEvent', field, value)}
          />

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <X className="w-4 h-4" />
              <span>Cancelar</span>
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSavingDraft || isSubmitting}
              className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{isSavingDraft ? 'Guardando...' : 'Guardar Borrador'}</span>
            </button>
            <button
              type="button"
              onClick={handlePreviewRecipients}
              disabled={isSubmitting || isSavingDraft}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors flex items-center space-x-2"
            >
              <Eye className="w-4 h-4" />
              <span>Aprobar y enviar correos</span>
            </button>
          </div>
        </div>

        {/* Modales */}
        <SelectionModals
          showModals={showModals}
          onCloseModal={(modalType) => {
            setShowModals(prev => ({ ...prev, [modalType]: false }));
            setCurrentDetailIndex(null);
          }}
          onSelect={(modalType, selectedItem) => {
            // Manejar selección según el tipo de modal
            if (modalType === 'origins') {
              handleChange('shipment', 'originId', selectedItem.id);
            } else if (modalType === 'destinations') {
              handleChange('shipment', 'destinationId', selectedItem.id);
            } else if (modalType === 'suppliers' && currentDetailIndex !== null) {
              // Agregar supplier al array (no reemplazar)
              const newDetails = [...formData.details];
              const currentSuppliers = newDetails[currentDetailIndex].suppliers || [];

              // Validar que no esté duplicado
              if (currentSuppliers.find(s => s.id === selectedItem.id)) {
                toast.error('Este proveedor ya fue agregado');
                return;
              }

              newDetails[currentDetailIndex] = {
                ...newDetails[currentDetailIndex],
                suppliers: [...currentSuppliers, selectedItem],
              };
              setFormData(prev => ({ ...prev, details: newDetails }));

              // NO cerrar modal para permitir agregar más
              // Modal permanece abierto
            } else if (modalType === 'consignees' && currentDetailIndex !== null) {
              if (selectedItem.id === 'manual') {
                // Activar modo manual para consignees
                const newDetails = [...formData.details];
                newDetails[currentDetailIndex] = {
                  ...newDetails[currentDetailIndex],
                  consigneeId: '',
                  isManualConsignee: true,
                  isTransportistaConsignee: false,
                  additional_consignees: '',
                  cif: false // 🔒 Desactivar CIF cuando se activa modo manual
                };
                setFormData(prev => ({ ...prev, details: newDetails }));
              } else {
                // Selección normal - detectar si es Transportista o Cliente
                const selectedUser = usersData?.users?.find(u => u.id === selectedItem.id);
                const isTransportista = selectedUser?.role?.role_name === 'Transportista';

                const newDetails = [...formData.details];
                newDetails[currentDetailIndex] = {
                  ...newDetails[currentDetailIndex],
                  consigneeId: selectedItem.id,
                  isManualConsignee: false,
                  isTransportistaConsignee: isTransportista,
                  // Si es Cliente, limpiar additional_consignees
                  additional_consignees: isTransportista ? newDetails[currentDetailIndex].additional_consignees : ''
                };
                setFormData(prev => ({ ...prev, details: newDetails }));
              }
            } else if (modalType === 'ff' && currentDetailIndex !== null) {
              handleChange('details', 'ff', selectedItem?.id || null, currentDetailIndex);
            } else if (modalType === 'seller' && currentDetailIndex !== null) {
              handleChange('details', 'seller', selectedItem?.id || null, currentDetailIndex);
            }
          }}
          originsData={originsData}
          destinationsData={destinationsData}
          suppliersData={suppliersData}
          usersData={usersData}
        />

        {/* Modal de Vista Previa de Destinatarios */}
        {showRecipientsPreview && (() => {
          const trimmedFormData = trimFormData(formData);
          return (
            <NewShipmentRecipientsModal
              isOpen={showRecipientsPreview}
              onClose={() => setShowRecipientsPreview(false)}
              shipment={{
                manifest: trimmedFormData.shipment.manifest,
                // 🔒 Mismo filtro que handleSubmit: las filas vacías no llegan al modal
                // (una fila vacía haría que getRecipientsPreviewTemp lance
                // "FF es obligatorio para consignatarios manuales")
                details: getValidDetails(trimmedFormData.details)
              }}
              statusDetail={trimmedFormData.initialEvent.status_detail}
              onConfirm={handleSubmit}
              user={user}
            />
          );
        })()}
      </div>
    </DashboardLayout>
  );
};

export default CreateNewShipment;